/**
 * GallerySection - Coverflow-style image carousel for the landing page.
 * Centre image is full-size and opaque; side images fade/shrink behind.
 * Supports arrow navigation, keyboard, swipe and infinite looping.
 */
import { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Badge } from '../ui/badge';
import { PhoneFrame } from './PhoneFrame';

const resolveUrl = (url) => {
  if (!url) return '';
  return url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
};

export function GallerySection({ content, theme, t }) {
  const gallery = content?.gallery || {};
  const images = (gallery.images || []).filter((img) => img && img.url);
  const [active, setActive] = useState(0);
  const [autoPlay, setAutoPlay] = useState(true);
  const [inView, setInView] = useState(false);
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const containerRef = useRef(null);

  const n = images.length;

  const goTo = useCallback((idx) => {
    if (n === 0) return;
    const next = ((idx % n) + n) % n;
    setActive(next);
  }, [n]);

  const advance = useCallback(() => {
    setActive((cur) => (n > 0 ? (cur + 1) % n : 0));
  }, [n]);

  const userGoTo = useCallback((idx) => {
    setAutoPlay(false);
    goTo(idx);
  }, [goTo]);

  const next = useCallback(() => { setAutoPlay(false); goTo(active + 1); }, [active, goTo]);
  const prev = useCallback(() => { setAutoPlay(false); goTo(active - 1); }, [active, goTo]);

  // Reset if images list shrinks below current index
  useEffect(() => {
    if (active >= n && n > 0) setActive(0);
  }, [n, active]);

  // Auto-rotate every 5s — only while the section is on screen and user hasn't interacted
  useEffect(() => {
    if (!autoPlay || !inView || n <= 1) return;
    const id = setInterval(advance, 5000);
    return () => clearInterval(id);
  }, [autoPlay, inView, n, advance]);

  // Track whether the section is on screen (for autoplay + keyboard nav)
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { setInView(entry.isIntersecting); },
      { threshold: 0.4 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // Keyboard nav when section is on screen
  useEffect(() => {
    const onKey = (e) => {
      if (!inView) return;
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [inView, next, prev]);

  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    const dy = e.changedTouches[0].clientY - (touchStartY.current ?? 0);
    touchStartX.current = null;
    touchStartY.current = null;
    // Only treat as swipe if primarily horizontal
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
      setAutoPlay(false);
      if (dx < 0) goTo(active + 1); else goTo(active - 1);
    }
  };

  // Compute offset from active, wrapped into [-floor(n/2), ceil(n/2)-1]
  const wrappedOffset = (i) => {
    if (n <= 1) return 0;
    let off = i - active;
    if (off > n / 2) off -= n;
    if (off < -n / 2) off += n;
    return off;
  };

  const slideStyle = (offset, mobile = false) => {
    const abs = Math.abs(offset);
    if (abs === 0) {
      return {
        transform: 'translateX(0%) scale(1)',
        opacity: 1,
        zIndex: 30,
        filter: 'blur(0px)',
      };
    }
    if (abs === 1) {
      // Tighter offset + smaller scale on mobile so side phones peek in.
      const tx = mobile ? offset * 46 : offset * 58;
      const sc = mobile ? 0.6 : 0.72;
      return {
        transform: `translateX(${tx}%) scale(${sc})`,
        opacity: mobile ? 0.55 : 0.45,
        zIndex: 20,
        filter: 'blur(1px)',
      };
    }
    if (abs === 2) {
      const tx = mobile ? offset * 60 : offset * 72;
      const sc = mobile ? 0.4 : 0.5;
      return {
        transform: `translateX(${tx}%) scale(${sc})`,
        opacity: 0.18,
        zIndex: 10,
        filter: 'blur(2px)',
      };
    }
    return {
      transform: `translateX(${offset > 0 ? 110 : -110}%) scale(0.4)`,
      opacity: 0,
      zIndex: 0,
      pointerEvents: 'none',
    };
  };

  const bgColor = theme === 'dark' ? (gallery.bgColorDark || null) : (gallery.bgColor || null);
  const textClass = theme === 'dark' ? 'text-white' : 'text-gray-900';
  const mutedClass = theme === 'dark' ? 'text-gray-400' : 'text-gray-500';

  // Track viewport so the phone frame can use a separate (usually smaller)
  // min-height on mobile devices. <=640px matches Tailwind's "sm" breakpoint.
  const [isMobileView, setIsMobileView] = useState(
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(max-width: 640px)').matches
      : false
  );
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia('(max-width: 640px)');
    const handler = (e) => setIsMobileView(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', handler);
      else mq.removeListener(handler);
    };
  }, []);

  // On mobile the configured value is a hard MAX (caps the phone height). On
  // desktop it's used as a min-height. Mobile has its own default (440) so a
  // tall desktop value never spills off a small viewport when the admin
  // hasn't set the mobile-specific field.
  const phoneMaxMobile = gallery.use_phone_frame && isMobileView
    ? (Number(gallery.phone_min_height_mobile_px) || 440)
    : null;
  const phoneMinDesktop = gallery.use_phone_frame && !isMobileView
    ? (Number(gallery.phone_min_height_px) || 560)
    : null;
  // Effective height used to reserve carousel space — equals the cap on
  // mobile, the min on desktop. Extra padding clears the bezel + dynamic
  // island and home-indicator at all admin-set values, plus an additional
  // 80px when the drop-shadow / reflection effect is enabled so the mirror
  // never gets clipped.
  const reflectionPad = gallery.use_phone_frame && gallery.phone_drop_shadow ? 80 : 0;
  // Less vertical chrome on mobile keeps the carousel + caption above the
  // fold; desktop keeps the existing generous spacing.
  const verticalPad = isMobileView ? 60 : 120;
  const phoneFrameMinH = phoneMaxMobile || phoneMinDesktop
    ? (phoneMaxMobile || phoneMinDesktop) + verticalPad + reflectionPad
    : null;

  if (n === 0) {
    return null;
  }

  const activeImage = images[active];

  return (
    <section
      id="gallery"
      data-section="gallery"
      className="py-12 sm:py-20 px-4 overflow-hidden"
      style={bgColor ? { backgroundColor: bgColor } : undefined}
      data-testid="gallery-section"
      ref={containerRef}
    >
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-6 sm:mb-12">
          {gallery.badge && (
            <Badge className="bg-[#007a33] text-white border-[#007a33] mb-4 px-4 py-1 hover:bg-[#007a33] text-[0.7rem] font-black tracking-[1px]" data-testid="gallery-badge">
              {gallery.badge}
            </Badge>
          )}
          {gallery.title && (
            <h2 className={`text-3xl sm:text-4xl font-bold mb-3 ${textClass}`} data-testid="gallery-title">{gallery.title}</h2>
          )}
          {gallery.subtitle && (
            <p className={`${mutedClass} text-lg`} data-testid="gallery-subtitle">{gallery.subtitle}</p>
          )}
        </div>

        {/* Carousel */}
        <div
          className={`relative flex items-center justify-center select-none ${phoneFrameMinH ? '' : 'min-h-[320px] sm:min-h-[420px] md:min-h-[480px]'}`}
          style={phoneFrameMinH ? { minHeight: `${phoneFrameMinH}px` } : undefined}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
          data-testid="gallery-carousel"
        >
          {images.map((img, i) => {
            const off = wrappedOffset(i);
            const style = slideStyle(off, isMobileView);
            const isActive = off === 0;
            const phoneOn = !!gallery.use_phone_frame;
            const imgFit = gallery.phone_image_fit === 'contain' ? 'object-contain' : 'object-cover';
            return (
              <button
                key={img.id || i}
                type="button"
                onClick={() => userGoTo(i)}
                className="absolute top-1/2 left-1/2 transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] bg-transparent p-0 border-0"
                style={{
                  ...style,
                  transform: `translate(-50%, -50%) ${style.transform}`,
                  cursor: isActive ? 'default' : 'pointer',
                }}
                data-testid={`gallery-slide-${i}`}
                aria-hidden={!isActive}
                aria-label={img.title || `Slide ${i + 1}`}
              >
                {phoneOn ? (
                  <PhoneFrame
                    bezelColor={gallery.phone_bezel_color}
                    width={gallery.phone_width_px}
                    minHeight={phoneMinDesktop}
                    maxHeight={phoneMaxMobile}
                    showStatusBar={gallery.phone_show_status_bar !== false}
                    compactStatusBar={true}
                    statusBarTimeMode={gallery.phone_time_mode || 'apple'}
                    statusBarTime={gallery.phone_status_time || '9:41'}
                    statusBarBg={gallery.phone_status_bar_bg}
                    statusBarIconColor={gallery.phone_status_icon_color}
                    showBrowserBar={gallery.phone_show_browser_bar !== false}
                    browserUrl={gallery.phone_browser_url || 'grassrootscoaching.app'}
                    browserBarBg={gallery.phone_browser_bar_bg}
                    screenBg={theme === 'dark' ? 'dark' : 'light'}
                    dropShadow={!!gallery.phone_drop_shadow}
                    floorShadow={gallery.phone_floor_shadow === true}
                    floorShadowOffset={gallery.phone_floor_shadow_offset || 0}
                  >
                    {/* Black wash behind the image hides the sub-pixel gap the
                        coverflow transform exposes between image edges and
                        the phone screen body (white in light theme, navy in
                        dark). Reads as "phone screen edge". */}
                    <div className="absolute inset-0 bg-black">
                      <img
                        src={resolveUrl(img.url)}
                        alt={img.title || `Slide ${i + 1}`}
                        className={`block w-full h-full ${imgFit}`}
                        draggable={false}
                        loading="lazy"
                        decoding="async"
                        fetchPriority={isActive ? 'high' : 'low'}
                      />
                    </div>
                  </PhoneFrame>
                ) : (
                  <img
                    src={resolveUrl(img.url)}
                    alt={img.title || `Slide ${i + 1}`}
                    className="block max-w-[82vw] sm:max-w-[62vw] md:max-w-[52vw] max-h-[320px] sm:max-h-[420px] md:max-h-[480px] w-auto h-auto"
                    draggable={false}
                    loading="lazy"
                    decoding="async"
                    fetchPriority={isActive ? 'high' : 'low'}
                  />
                )}
              </button>
            );
          })}

          {/* Arrows — hidden on touch devices (swipe instead) */}
          {n > 1 && (
            <div className="gallery-arrows">
              <button
                type="button"
                onClick={prev}
                className={`absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-40 p-2.5 rounded-full shadow-lg transition-all ${
                  theme === 'dark' ? 'bg-gray-800/80 hover:bg-gray-700 text-white' : 'bg-white/90 hover:bg-white text-gray-800'
                }`}
                aria-label="Previous image"
                data-testid="gallery-prev"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                type="button"
                onClick={next}
                className={`absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-40 p-2.5 rounded-full shadow-lg transition-all ${
                  theme === 'dark' ? 'bg-gray-800/80 hover:bg-gray-700 text-white' : 'bg-white/90 hover:bg-white text-gray-800'
                }`}
                aria-label="Next image"
                data-testid="gallery-next"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>

        {/* Active title + subtitle (animated) */}
        <div className="mt-4 sm:mt-8 flex flex-col items-center justify-center gap-1 text-center">
          <h3
            key={`t-${active}`}
            className={`text-lg sm:text-xl font-semibold ${textClass} gallery-title-fade min-h-[1.75rem]`}
            data-testid="gallery-active-title"
          >
            {activeImage?.title || ''}
          </h3>
          {activeImage?.subtitle ? (
            <p
              key={`s-${active}`}
              className={`text-sm sm:text-base ${mutedClass} gallery-title-fade`}
              data-testid="gallery-active-subtitle"
            >
              {activeImage.subtitle}
            </p>
          ) : null}
        </div>

        {/* Dots */}
        {n > 1 && (
          <div className="mt-4 flex items-center justify-center gap-2" data-testid="gallery-dots">
            {images.map((img, i) => (
              <button
                key={img.id || i}
                type="button"
                onClick={() => userGoTo(i)}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === active
                    ? 'w-8 bg-[#007a33]'
                    : theme === 'dark'
                    ? 'w-2 bg-gray-600 hover:bg-gray-500'
                    : 'w-2 bg-gray-300 hover:bg-gray-400'
                }`}
                aria-label={`Go to slide ${i + 1}`}
                data-testid={`gallery-dot-${i}`}
              />
            ))}
          </div>
        )}
      </div>

      <style>{`
        @keyframes galleryTitleFade {
          0% { opacity: 0; transform: translateY(8px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .gallery-title-fade {
          animation: galleryTitleFade 0.5s ease-out both;
        }
        @media (hover: none) and (pointer: coarse), (max-width: 640px) {
          .gallery-arrows { display: none; }
        }
      `}</style>
    </section>
  );
}

export default GallerySection;
